/**
 * @fileoverview Persistent warning banner for users with unverified email addresses.
 * Shows a yellow banner prompting them to verify, with a resend button.
 * Dismissable per session via local state.
 */

import React, { useState } from 'react';
import api from '../services/api';

/**
 * EmailVerificationBanner component.
 * Renders a dismissable yellow warning banner when the user's email is not verified.
 * Provides a "Resend Verification Email" button that calls the backend.
 *
 * @returns {JSX.Element|null} The banner element, or null if dismissed
 */
function EmailVerificationBanner() {
  const [dismissed, setDismissed] = useState(false);
  const [resendStatus, setResendStatus] = useState('');
  const [sending, setSending] = useState(false);

  if (dismissed) return null;

  /**
   * Handle resend verification email button click.
   * Calls the resend-verification API endpoint.
   */
  const handleResend = async () => {
    setSending(true);
    setResendStatus('');
    try {
      await api.resendVerification({});
      setResendStatus('Verification email sent! Check your inbox.');
    } catch {
      setResendStatus('Failed to send. Please try again later.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="email-verification-banner"
      role="alert"
      style={{
        background: '#fef3cd',
        borderBottom: '1px solid #ffc107',
        padding: '10px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        fontSize: '14px',
        color: '#856404',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <span>Your email is not verified. Some features are restricted.</span>
        {resendStatus && (
          <span style={{ fontWeight: 500 }}>{resendStatus}</span>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
        <button
          onClick={handleResend}
          disabled={sending}
          style={{
            background: '#856404',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            padding: '4px 12px',
            cursor: sending ? 'not-allowed' : 'pointer',
            fontSize: '13px',
          }}
        >
          {sending ? 'Sending...' : 'Resend Verification Email'}
        </button>
        <button
          onClick={() => setDismissed(true)}
          aria-label="Dismiss verification banner"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#856404',
            fontSize: '18px',
            lineHeight: 1,
            padding: '0 4px',
          }}
        >
          &times;
        </button>
      </div>
    </div>
  );
}

export default EmailVerificationBanner;
